/**
 * Uji sumur buta, digambar: tiap sumur buta sebuah model, kelas asli dari
 * penggolong di sebelah kelas yang ditaksir ML, dan strip ≠ tempat keduanya
 * berbeda.
 *
 * Akurasi buta satu angka untuk seluruh sumur; lembar ini menjawab pertanyaan
 * sesudahnya — di sumur mana, di kedalaman mana, dan apakah net pay-nya ikut
 * bergeser. Tiap sumur memakai sumbu kedalamannya sendiri, sebab sumur buta
 * satu lapangan bisa berselisih ratusan meter puncaknya.
 */

import { useState } from "react";
import { api } from "../api";
import { useAsync } from "../hooks";
import { Empty, Loading, Notice, Segmented } from "./ui";
import { Swatch, niceTicks, pct, type Band } from "./LrlcEstimateViews";

export interface BlindWell {
  well: string;
  field: string;
  error?: string;
  accuracy?: number;
  samples?: number;
  net_pay_truth?: number | null;
  net_pay_ml?: number | null;
  net_pay_whole_well?: number | null;
  depth_range?: [number, number];
  truth?: Band[];
  predicted?: Band[];
  differs?: [number, number][];
}

export interface BlindSheetPayload {
  model: string;
  kind_label: string;
  inputs: string[];
  order: "pay" | "worst" | "name";
  blind_total: number;
  shown: number;
  blind_score: { value: number | null; extra: number | null };
  wells: BlindWell[];
}

const H = 540;
const COL = 30;
const AXIS = 30;
const DIFF = "#e0452b";

export default function BlindSheet({ modelId }: { modelId: number }) {
  const [order, setOrder] = useState<"pay" | "worst" | "name">("pay");
  const [count, setCount] = useState("16");
  const sheet = useAsync(
    (signal) => api.get<BlindSheetPayload>(`/api/log-models/${modelId}/blind-sheet/`,
                                           { order, count }, signal),
    [modelId, order, count]);

  const wells = sheet.data?.wells ?? [];
  const legend = new Map<number, Band>();
  for (const w of wells) {
    for (const b of [...(w.truth ?? []), ...(w.predicted ?? [])]) {
      if (!legend.has(b.code)) legend.set(b.code, b);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8,
                  border: "1px solid var(--line)", borderRadius: 7, padding: 10,
                  background: "var(--panel)" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ font: "600 11.5px var(--font-sans)", color: "var(--ink-2)" }}>
          Blind test, drawn
        </span>
        <Segmented value={order} size="sm" onChange={(v) => setOrder(v as typeof order)}
                   options={[
                     { value: "pay", label: "Most net pay" },
                     { value: "worst", label: "Worst first" },
                     { value: "name", label: "By name" },
                   ]} />
        <Segmented value={count} size="sm" onChange={setCount}
                   options={["8", "16", "32", "40"].map((n) => ({ value: n, label: `${n} wells` }))} />
      </div>

      {sheet.loading && <Loading label="Estimating the blind wells…" />}
      {sheet.error && <Notice tone="error">{sheet.error}</Notice>}
      {sheet.data && wells.length === 0 && (
        <Empty>This model has no blind well that still carries its classes.</Empty>
      )}
      {sheet.data && wells.length > 0 && (
        <>
          <div style={{ textAlign: "center", font: "500 11px var(--font-sans)",
                        color: "var(--ink-3)" }}>
            {sheet.data.model} — classes from the rules against {sheet.data.kind_label} from{" "}
            {sheet.data.inputs.join(", ")}
            <div style={{ font: "400 10px var(--font-sans)", color: "var(--ink-6)" }}>
              {sheet.data.shown} of {sheet.data.blind_total} blind wells ·{" "}
              {{ pay: "most net pay first", worst: "lowest accuracy first",
                 name: "by name" }[sheet.data.order]}{" "}
              · heading: accuracy, net pay rules / ML
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }}
               aria-label="Blind test sheet">
            {wells.map((w) => <WellPanel key={w.well} w={w} />)}
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center",
                        font: "400 10px var(--font-mono)", color: "var(--ink-5)" }}>
            {[...legend.values()].sort((a, b) => a.code - b.code).map((b) => (
              <Swatch key={b.code} color={b.color} label={b.label} />
            ))}
            <Swatch color={DIFF} label="≠ depths where the class differs" />
          </div>
          <span style={{ font: "400 10px/1.6 var(--font-sans)", color: "var(--ink-6)" }}>
            Rules is LRLC_CLASS from the classifier; ML is what the model gives the
            same samples. These wells were held out of training. Net pay is the
            thickness of classes 2–5 on the samples both carry, so the two numbers
            differ by class and not by coverage. Whole-model blind accuracy{" "}
            {pct(sheet.data.blind_score.value)}.
          </span>
        </>
      )}
    </div>
  );
}

function WellPanel({ w }: { w: BlindWell }) {
  const width = AXIS + COL * 2 + 4 + 10;
  if (w.error || !w.depth_range) {
    return (
      <div style={{ width, flex: "none", font: "400 10px var(--font-sans)", color: "var(--rose)" }}>
        <b>{w.well}</b><br />{w.error}
      </div>
    );
  }
  const [top, base] = w.depth_range;
  const y = (md: number) => ((md - top) / Math.max(1e-6, base - top)) * H;
  const draw = (list: Band[], x: number) => list.map((b, i) => {
    const y0 = y(b.top);
    return (
      <rect key={i} x={x} width={COL} y={y0} height={Math.max(0.6, y(b.base) - y0)} fill={b.color}>
        <title>{`${b.label} · ${b.top.toFixed(1)}–${b.base.toFixed(1)} m`}</title>
      </rect>
    );
  });
  return (
    <div style={{ flex: "none", width }} aria-label={`Blind well ${w.well}`}>
      <div style={{ textAlign: "center", font: "400 9.5px/1.35 var(--font-sans)",
                    color: "var(--ink-4)", height: 42 }}>
        <div style={{ font: "600 10.5px var(--font-sans)", color: "var(--ink-2)" }}>{w.well}</div>
        accuracy {pct(w.accuracy)}
        {w.net_pay_truth != null && (
          <div>net pay {Math.round(w.net_pay_truth)} / {Math.round(w.net_pay_ml ?? 0)} m</div>
        )}
      </div>
      <svg width={width} height={H + 16} style={{ display: "block" }}>
        <rect x={AXIS} y={0} width={COL * 2 + 4} height={H} fill="var(--panel-inset)"
              stroke="var(--line)" />
        {niceTicks(top, base).map((tick) => (
          <g key={tick}>
            <line x1={AXIS - 3} x2={AXIS} y1={y(tick)} y2={y(tick)} stroke="var(--ink-6)" />
            <text x={AXIS - 4} y={y(tick) + 3} textAnchor="end"
                  style={{ font: "8.5px var(--font-mono)", fill: "var(--ink-5)" }}>{tick}</text>
          </g>
        ))}
        {draw(w.truth ?? [], AXIS)}
        {draw(w.predicted ?? [], AXIS + COL + 4)}
        {(w.differs ?? []).map(([s0, s1], i) => (
          <rect key={i} x={AXIS + COL * 2 + 6} width={8} y={y(s0)}
                height={Math.max(0.8, y(s1) - y(s0))} fill={DIFF} />
        ))}
        <text x={AXIS + COL / 2} y={H + 12} textAnchor="middle"
              style={{ font: "9px var(--font-sans)", fill: "var(--ink-5)" }}>Rules</text>
        <text x={AXIS + COL * 1.5 + 4} y={H + 12} textAnchor="middle"
              style={{ font: "9px var(--font-sans)", fill: "var(--ink-5)" }}>ML</text>
        <text x={AXIS + COL * 2 + 10} y={H + 12} textAnchor="middle"
              style={{ font: "9px var(--font-sans)", fill: "var(--ink-5)" }}>≠</text>
      </svg>
    </div>
  );
}
